import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import { Box, Heading, Collection } from 'lucid-components-react';
import React, { useContext } from 'react';

import Link from 'components/link/link';
import LucidContainer from 'components/lucid-container/lucid-container';
import LayoutContext from 'contexts/layoutContext';
import { Text } from 'theme/typography';
import {
  Drupal_ParagraphAdditionalResources,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphMarketoForm
} from 'generated/graphql';
import useMarketoLink from 'hooks/use-marketo-link';

interface AdditionalResourcesProps {
  resources: Drupal_ParagraphAdditionalResources[];
  blurb?: string | null | undefined;
  ctas: Drupal_ParagraphCtaButton[];
}

const AdditionalResources = ({
  resources,
  blurb,
  ctas
}: AdditionalResourcesProps) => {
  const { language } = useContext(LayoutContext);

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_ct_cs_additional_resources
                mktg_ct_cs_prefooter_title
                mktg_ct_all_get_a_quote
                mktg_ct_all_request_a_demo
                mtkg_submit_policy
              }
            }
          }
        }
      }
    `
  );

  const i18n = i18nFunct(i18nData, language.i18nKey);
  const getMarketoLink = useMarketoLink(language);

  return (
    <Box
      as="section"
      css={theme => ({
        backgroundColor: theme.colors.coolGrey[90],
        backgroundImage: 'url("https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/consideration/background/footer-background.png")',
        backgroundPositionX: '-120px',
        backgroundPositionY: '40%',
        backgroundSize: '100%',
        color: theme.colors.white,
        padding: `${theme.space[8]}px 0`,
      })}
    >
      <LucidContainer flex css={{ flexWrap: 'wrap' }}>
        <Box flex={{ default: '0 1 100%', tabletSmall: '0 1 41.6%' }} mb={4}>
          <Text css={theme => ({ color: theme.colors.white, marginTop: 0 })} textStyles="body">
            {i18n('mktg-ct-cs-additional-resources')}
          </Text>
          <ul css={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {resources
              .filter(ar => ar.fieldResourceUrl)
              .map(ar => {
                return (
                  <li key={ar.fieldResourceText}>
                    <Link inverse css={theme => ({ marginBottom: theme.space[2] })} to={ar.fieldResourceUrl!}>
                      {ar.fieldResourceText}
                    </Link>
                  </li>
                );
              })}
          </ul>
        </Box>
        <Box flex={{ default: '0 1 100%', tabletSmall: '0 1 55.3%' }}>
          <Heading as="h2" color="white" variant="subheadS">
            {blurb || i18n('mktg-ct-cs-prefooter-title')}
          </Heading>
          <Collection hSpace={3} vSpace={3}>
            {ctas.map((cta, i) => {
              const buttonText = cta.fieldButtonText;
              const defaultText =
                i === 0
                  ? i18n('mktg-ct-all-get-a-quote')
                  : i18n('mktg-ct-all-request-a-demo');
              const fieldMarketo = cta.fieldMarketo
                ? (cta.fieldMarketo.entity as Drupal_ParagraphMarketoForm)
                : null;
              if (cta.fieldButtonLink) {
                return (
                  <Link
                    key={cta.fieldButtonLink}
                    inverse
                    secondary
                    className="additional-resources__cta-link"
                    to={cta.fieldButtonLink}
                  >
                    {buttonText || defaultText}
                  </Link>
                );
              }
              if (fieldMarketo?.fieldMarketoId) {
                const { url, copy } = getMarketoLink(
                  fieldMarketo.fieldMarketoId
                )!;
                return (
                  <Link
                    key={fieldMarketo.fieldMarketoId}
                    asButton
                    primary={!cta.fieldIsSecondary}
                    secondary={!!cta.fieldIsSecondary}
                    to={url}
                  >
                    {buttonText || copy}
                  </Link>
                );
              }
              return;
            })}
          </Collection>
        </Box>
      </LucidContainer>
    </Box>
  );
};

export default AdditionalResources;
